import React, { memo } from 'react';
import clsx from 'clsx';
import { Map, List } from 'immutable';
import { useContextMenu } from '../contexts/ContextMenuContext';
import { normalizeForSearch } from '../utils/searchUtils';
import { copyToClipboard } from '../utils/copyToClipboard';
import PrimitiveValue from './PrimitiveValue';

interface StateNodeProps {
    nodeKey: string;
    nodeValue: any;
    path: string;
    searchPath: string | null;
    matchingPaths?: string[];
    nestLevel: number;
    isExpanded: boolean;
    onToggle: (key: string, path?: string | null) => void;
    renderValue: (
        value: any,
        nestLevel?: number,
        path?: string,
    ) => React.ReactNode;
    onCollapseChildren: (path: string) => void;
    onMouseEnter?: (path: string) => void;
    onMouseLeave?: () => void;
}

/**
 * Component for rendering a state node in the tree
 * @param props - Component props
 * @returns Rendered component
 */
const StateNode = memo(
    ({
        nodeKey,
        nodeValue,
        path,
        searchPath,
        matchingPaths = [],
        nestLevel,
        isExpanded,
        onToggle,
        renderValue,
        onCollapseChildren,
        onMouseEnter,
        onMouseLeave,
    }: StateNodeProps) => {
        const { openContextMenu } = useContextMenu();
        const keyPath = path ? `${path}.${nodeKey}` : nodeKey;

        // Check if the key matches the search using normalized comparison
        const isKeyMatch =
            searchPath &&
            (normalizeForSearch(keyPath).includes(
                normalizeForSearch(searchPath),
            ) ||
                normalizeForSearch(searchPath).includes(
                    normalizeForSearch(keyPath),
                ));

        // Check if non-object value matches search
        const isPrimitiveValue =
            typeof nodeValue !== 'object' ||
            nodeValue === null ||
            (Array.isArray(nodeValue) && nodeValue.length === 0);

        // Use safer value matching
        const isValueMatch =
            isPrimitiveValue &&
            searchPath &&
            normalizeForSearch(String(nodeValue)).includes(
                normalizeForSearch(searchPath),
            );

        // Check for exact ID match - stronger highlight for exact matches
        const isIdMatch =
            searchPath &&
            (normalizeForSearch(nodeKey) === normalizeForSearch(searchPath) ||
                normalizeForSearch(String(nodeValue)) ===
                    normalizeForSearch(searchPath) ||
                (normalizeForSearch(nodeKey).includes(
                    normalizeForSearch(searchPath),
                ) &&
                    (nodeKey.includes('-') || /^\d+$/.test(searchPath))));

        const isMatch = isKeyMatch || isValueMatch || isIdMatch;

        // Determine if this is an expandable value
        const isExpandable =
            typeof nodeValue === 'object' &&
            nodeValue !== null &&
            (Object.keys(nodeValue).length > 0 ||
                (Array.isArray(nodeValue) && nodeValue.length > 0) ||
                (Map.isMap(nodeValue) && nodeValue.size > 0) ||
                (List.isList(nodeValue) && nodeValue.size > 0));

        // Only auto-expand in very specific cases:
        // 1. Exact match for path searches (e.g. "app.extension" exactly)
        // 2. Direct ID matches
        const isExactPathMatch =
            searchPath &&
            searchPath.includes('.') &&
            path &&
            normalizeForSearch(`${path}.${nodeKey}`) ===
                normalizeForSearch(searchPath);

        // Check if this node's path is on the way to a matching value
        const isOnPathToMatch = matchingPaths.some((matchPath) => {
            // Only consider exact path matches or direct parent paths
            if (matchPath === keyPath) return true;

            // For matches with array indices, ensure the numbers match exactly
            if (
                matchPath.startsWith(path + '.') &&
                keyPath.startsWith(path + '.')
            ) {
                const matchSuffix = matchPath.substring(path.length + 1);
                const keySuffix = keyPath.substring(path.length + 1);

                // If the suffixes start with different numbers, they're different indices
                if (
                    !isNaN(Number(matchSuffix[0])) &&
                    !isNaN(Number(keySuffix[0])) &&
                    matchSuffix[0] !== keySuffix[0]
                ) {
                    return false;
                }
            }

            return (
                matchPath.startsWith(keyPath + '.') ||
                (path &&
                    matchPath.startsWith(path + '.') &&
                    matchPath.includes(`.${nodeKey}.`))
            );
        });

        // Auto-expand if this node is on the path to a matching value
        const shouldAutoExpand =
            isExactPathMatch || isIdMatch || isOnPathToMatch;

        // Use the controlled isExpanded value or auto-expand only on exact matches
        const effectiveIsExpanded = isExpanded || shouldAutoExpand;

        // Handle right click for context menu
        const handleContextMenu = (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            const menuItems = [
                {
                    label: 'Copy Value',
                    action: () => copyToClipboard(nodeValue),
                },
                {
                    label: 'Copy with Key',
                    action: () => copyToClipboard(nodeValue, nodeKey),
                },
                {
                    label: 'Copy Full Path',
                    action: () => copyToClipboard(nodeValue, null, keyPath),
                },
            ];

            // Add collapse option only for expandable items
            if (isExpandable) {
                menuItems.push({
                    label: 'Collapse Children', // Back to original text that better describes the behavior
                    action: () => onCollapseChildren(keyPath),
                });
            }

            openContextMenu({
                x: e.clientX,
                y: e.clientY,
                menuItems,
            });
        };

        return (
            <div
                className={clsx('my-1', isMatch && 'rounded-sm bg-gray-100')}
                onContextMenu={handleContextMenu}
                onMouseEnter={() => {
                    if (onMouseEnter) onMouseEnter(keyPath);
                }}
                onMouseLeave={() => {
                    if (onMouseLeave) onMouseLeave();
                }}
            >
                <div className="flex items-start justify-between py-2 hover:bg-gray-200">
                    {isExpandable ? (
                        <>
                            <div
                                onClick={() => onToggle(nodeKey)}
                                className={clsx(
                                    'ml-4 flex cursor-pointer font-semibold hover:text-gray-600',
                                    isMatch && 'text-green font-bold',
                                )}
                                onMouseEnter={() =>
                                    onMouseEnter && onMouseEnter(keyPath)
                                }
                                onMouseLeave={() =>
                                    onMouseLeave && onMouseLeave()
                                }
                            >
                                <div className="w-6">
                                    {effectiveIsExpanded ? '📂' : '📁'}
                                </div>
                                {nodeKey}:
                            </div>
                            <div className="flex items-center">
                                {!effectiveIsExpanded && (
                                    <div className="ml-4 max-w-xs truncate text-gray-500">
                                        {Map.isMap(nodeValue)
                                            ? `Map(${nodeValue.size})`
                                            : List.isList(nodeValue)
                                              ? `List(${nodeValue.size})`
                                              : Array.isArray(nodeValue)
                                                ? `Array(${nodeValue.length})`
                                                : `Object(${Object.keys(nodeValue).length})`}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="ml-10 flex flex-1 flex-wrap items-center justify-between gap-x-1">
                            <div
                                className={clsx(
                                    '',
                                    isKeyMatch && 'text-green font-bold',
                                )}
                            >
                                {nodeKey}
                            </div>
                            <div className="flex items-center">
                                <div className="rounded-sm border bg-gray-100 p-1">
                                    <PrimitiveValue
                                        value={nodeValue}
                                        searchPath={searchPath}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                {isExpandable && effectiveIsExpanded && (
                    <div className="pl-4">
                        {renderValue(nodeValue, nestLevel + 1, keyPath)}
                    </div>
                )}
            </div>
        );
    },
);

// Make sure we're exporting the component correctly
export default StateNode;
